import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RinkDiagram } from "../components/RinkDiagram";
import {
  AGE_GROUPS,
  DRILL_FOCUS_AREAS,
  type Drill,
  type DrillFocusArea,
  type MarkerKind,
  type PathEnding,
  type PathKind,
} from "../domain/drill";
import { saveCloudPracticePlan } from "../lib/cloud-practice-storage";
import { loadDrills } from "../lib/drill-storage";
import { createPdfFile, sharePdfFile } from "../lib/pdf-share";
import { renderPathGeometry } from "../lib/path-rendering";
import {
  emptyStation,
  loadPracticeTemplates,
  loadPlan,
  practiceDurationMinutes,
  savePlan,
  savePracticeTemplate,
  usePracticeTemplate,
  type PracticePlan,
  type PracticeStation,
} from "../lib/practice-storage";

const formatPracticeDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || "—";
};

function FormattedPracticeText({ value }: { value: string }) {
  const inline = (line: string) =>
    line
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part, index) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={index}>{part.slice(2, -2)}</strong>
        ) : (
          part
        ),
      );
  const lines = value.split("\n");
  const blocks: React.ReactNode[] = [];
  for (let index = 0; index < lines.length;) {
    const numbered = lines[index].match(/^\s*\d+\.\s+(.*)$/);
    const bullet = lines[index].match(/^\s*[-•]\s+(.*)$/);
    if (numbered || bullet) {
      const items: string[] = [];
      const ordered = Boolean(numbered);
      while (index < lines.length) {
        const match = lines[index].match(
          ordered ? /^\s*\d+\.\s+(.*)$/ : /^\s*[-•]\s+(.*)$/,
        );
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      const children = items.map((item, itemIndex) => (
        <li key={itemIndex}>{inline(item)}</li>
      ));
      blocks.push(
        ordered ? (
          <ol key={blocks.length}>{children}</ol>
        ) : (
          <ul key={blocks.length}>{children}</ul>
        ),
      );
      continue;
    }
    blocks.push(<p key={blocks.length}>{inline(lines[index]) || <br />}</p>);
    index += 1;
  }
  return <div className="formatted-notes-output">{blocks}</div>;
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const inlineMarkdownHtml = (value: string) =>
  escapeHtml(value).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

function markdownToEditorHtml(value: string) {
  if (!value) return "";
  const lines = value.split("\n");
  const blocks: string[] = [];
  for (let index = 0; index < lines.length;) {
    const numbered = lines[index].match(/^\s*\d+\.\s+(.*)$/);
    const bullet = lines[index].match(/^\s*[-•]\s+(.*)$/);
    if (numbered || bullet) {
      const ordered = Boolean(numbered);
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index].match(
          ordered ? /^\s*\d+\.\s+(.*)$/ : /^\s*[-•]\s+(.*)$/,
        );
        if (!match) break;
        items.push(`<li>${inlineMarkdownHtml(match[1])}</li>`);
        index += 1;
      }
      blocks.push(
        ordered ? `<ol>${items.join("")}</ol>` : `<ul>${items.join("")}</ul>`,
      );
      continue;
    }
    blocks.push(`<div>${inlineMarkdownHtml(lines[index]) || "<br>"}</div>`);
    index += 1;
  }
  return blocks.join("");
}

function editorToMarkdown(root: HTMLElement) {
  const inline = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (!(node instanceof HTMLElement)) return "";
    const content = [...node.childNodes].map(inline).join("");
    if (node.tagName === "STRONG" || node.tagName === "B")
      return `**${content}**`;
    if (node.tagName === "BR") return "\n";
    return content;
  };
  const blocks = [...root.childNodes].map((node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (!(node instanceof HTMLElement)) return "";
    if (node.tagName === "UL" || node.tagName === "OL") {
      const ordered = node.tagName === "OL";
      return [...node.children]
        .filter((child) => child.tagName === "LI")
        .map(
          (item, index) =>
            `${ordered ? `${index + 1}.` : "-"} ${[...item.childNodes]
              .map(inline)
              .join("")}`,
        )
        .join("\n");
    }
    return [...node.childNodes].map(inline).join("");
  });
  return blocks
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

function PracticeTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const boldTyping = useRef(false);
  useEffect(() => {
    if (!editor.current || editor.current.contains(document.activeElement))
      return;
    const html = markdownToEditorHtml(value);
    if (editor.current.innerHTML !== html) editor.current.innerHTML = html;
  }, [value]);

  const syncValue = () => {
    if (editor.current) onChange(editorToMarkdown(editor.current));
  };

  const applyFormat = (
    command: "bold" | "insertOrderedList" | "insertUnorderedList",
  ) => {
    const surface = editor.current;
    if (!surface) return;
    if (!surface.contains(document.activeElement)) {
      surface.focus();
      const range = document.createRange();
      range.selectNodeContents(surface);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    const selection = window.getSelection();
    const isTypingToggle =
      command === "bold" && Boolean(selection?.isCollapsed);
    document.execCommand(command, false);
    boldTyping.current = isTypingToggle && document.queryCommandState("bold");
    syncValue();
  };

  const cancelBoldTyping = () => {
    if (!boldTyping.current) return;
    if (document.queryCommandState("bold")) document.execCommand("bold", false);
    boldTyping.current = false;
  };

  return (
    <div className="practice-text-editor">
      <div
        className="format-toolbar no-print"
        aria-label="Description formatting"
      >
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyFormat("bold")}
          title="Bold"
          aria-label="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyFormat("insertOrderedList")}
          title="Numbered list"
          aria-label="Numbered list"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 6h11M9 12h11M9 18h11" />
            <text x="2.5" y="8">
              1
            </text>
            <text x="2.5" y="14">
              2
            </text>
            <text x="2.5" y="20">
              3
            </text>
          </svg>
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyFormat("insertUnorderedList")}
          title="Bullet list"
          aria-label="Bullet list"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 6h11M9 12h11M9 18h11" />
            <circle cx="4" cy="6" r="1.4" />
            <circle cx="4" cy="12" r="1.4" />
            <circle cx="4" cy="18" r="1.4" />
          </svg>
        </button>
      </div>
      <div
        ref={editor}
        className="practice-rich-editor"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label="Description"
        aria-multiline="true"
        data-placeholder="Organisation, flow and progressions…"
        onPointerDown={cancelBoldTyping}
        onInput={syncValue}
        onBlur={syncValue}
      />
      <FormattedPracticeText value={value} />
    </div>
  );
}

function PracticeMarkerLegendIcon({ kind }: { kind: MarkerKind }) {
  const stroke = "#102032";
  return (
    <svg
      className="practice-legend-icon"
      viewBox="0 0 32 24"
      aria-hidden="true"
    >
      {kind === "coach" && (
        <>
          <circle
            cx="16"
            cy="12"
            r="9"
            fill="white"
            stroke={stroke}
            strokeWidth="2"
          />
          <text x="16" y="15.5" textAnchor="middle">
            C
          </text>
        </>
      )}
      {kind === "goalie" && (
        <>
          <circle cx="16" cy="12" r="9" fill={stroke} />
          <text className="inverse" x="16" y="15.5" textAnchor="middle">
            G
          </text>
        </>
      )}
      {kind === "player" && (
        <>
          <circle cx="16" cy="12" r="9" fill={stroke} />
          <text className="inverse" x="16" y="15.5" textAnchor="middle">
            F
          </text>
        </>
      )}
      {kind === "defender" && (
        <>
          <path d="M16 2L27 21H5Z" fill={stroke} />
          <text className="inverse" x="16" y="17" textAnchor="middle">
            D
          </text>
        </>
      )}
      {kind === "cone" && (
        <path
          d="M9 20H23L17 4H15ZM7 21H25"
          fill="none"
          stroke="#ee7b21"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      )}
      {kind === "puckCluster" &&
        [
          [12, 7],
          [20, 7],
          [8, 12],
          [16, 12],
          [24, 12],
          [12, 17],
          [20, 17],
        ].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.8" fill={stroke} />
        ))}
      {kind === "tire" && (
        <>
          <circle
            cx="16"
            cy="12"
            r="8"
            fill="none"
            stroke={stroke}
            strokeWidth="3.5"
          />
          <circle cx="16" cy="12" r="2.2" fill={stroke} />
        </>
      )}
      {kind === "barrier" && (
        <rect x="5" y="8" width="22" height="8" rx="1.5" fill="#df3344" />
      )}
    </svg>
  );
}

function PracticeLineLegendIcon({
  kind,
  ending = "arrow",
}: {
  kind: PathKind;
  ending?: PathEnding;
}) {
  const geometry = renderPathGeometry(kind, [
    { x: 5, y: 12 },
    { x: 61, y: 12 },
  ]);
  return (
    <svg
      className="practice-legend-line"
      viewBox="0 0 76 24"
      aria-hidden="true"
    >
      <path d={geometry.primary} strokeDasharray={geometry.dash} />
      {geometry.secondary && <path d={geometry.secondary} />}
      {geometry.ticks && <path d={geometry.ticks} />}
      {ending === "arrow" && <path d="M55 6L65 12L55 18" />}
      {ending === "shot" && <path d="M48 8H57M48 16H57M57 5L67 12L57 19" />}
      {ending === "stop" && <path d="M59 5V19M65 5V19" />}
      {ending === "pivot" && (
        <>
          <path d="M55 13A8 8 0 1 1 63 20" />
          <path className="filled" d="M59 20L66 16L66 23Z" />
        </>
      )}
    </svg>
  );
}

function PracticeLegend() {
  return (
    <div className="practice-legend" aria-label="Drill symbol legend">
      <strong>Legend</strong>
      <div className="practice-legend-grid">
        <span>
          <PracticeMarkerLegendIcon kind="coach" /> Coach
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="player" /> Forward
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="defender" /> Defender
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="goalie" /> Goalie
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="cone" /> Cone
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="puckCluster" /> Pucks
        </span>
        <span>
          <PracticeLineLegendIcon kind="free" /> Skating
        </span>
        <span>
          <PracticeLineLegendIcon kind="skatePuck" /> Puck control
        </span>
        <span>
          <PracticeLineLegendIcon kind="pass" /> Passing
        </span>
        <span>
          <PracticeLineLegendIcon kind="straight" ending="shot" /> Shooting
        </span>
        <span>
          <PracticeLineLegendIcon kind="backward" /> Backwards
        </span>
        <span>
          <PracticeLineLegendIcon kind="lateral" /> Lateral
        </span>
        <span>
          <PracticeLineLegendIcon kind="straight" ending="pivot" /> Pivot
        </span>
        <span>
          <PracticeLineLegendIcon kind="straight" ending="stop" /> Stop
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="tire" /> Tire
        </span>
        <span>
          <PracticeMarkerLegendIcon kind="barrier" /> Barrier
        </span>
      </div>
    </div>
  );
}

export function PracticePlannerRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const drills = useMemo(() => loadDrills(), []);
  const [plan, setPlan] = useState<PracticePlan>(loadPlan);
  const [shareStatus, setShareStatus] = useState<
    "" | "preparing" | "downloaded" | "error"
  >("");
  const shareFile = useRef<Promise<File> | undefined>(undefined);
  const [browseStation, setBrowseStation] = useState<number | null>(null);
  const [browseQuery, setBrowseQuery] = useState("");
  const [templateSaved, setTemplateSaved] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(() =>
    searchParams.get("template"),
  );
  const [browseCategory, setBrowseCategory] = useState<"all" | DrillFocusArea>(
    "all",
  );
  const drillById = useMemo(
    () => new Map(drills.map((drill) => [drill.id, drill])),
    [drills],
  );
  const eligibleDrills = useMemo(
    () => drills.filter((drill) => drill.ageGroups.includes(plan.ageGroup)),
    [drills, plan.ageGroup],
  );
  const browsableDrills = useMemo(() => {
    const query = browseQuery.trim().toLowerCase();
    return eligibleDrills.filter(
      (drill) =>
        (browseCategory === "all" ||
          drill.focusAreas.includes(browseCategory)) &&
        (!query ||
          `${drill.title} ${drill.instructions} ${drill.coachingPoints} ${drill.folder} ${drill.focusAreas.join(" ")}`
            .toLowerCase()
            .includes(query)),
    );
  }, [browseCategory, browseQuery, eligibleDrills]);
  const stationMinutes = plan.stations.reduce(
    (total, station) => total + station.durationMinutes,
    0,
  );
  const totalMinutes = practiceDurationMinutes(
    plan.startTime,
    plan.endTime,
    stationMinutes,
  );

  const prepareShareFile = () => {
    if (!shareFile.current) {
      const source = document.querySelector<HTMLElement>(".practice-document");
      if (!source) return;
      setShareStatus("preparing");
      shareFile.current = createPdfFile(
        source,
        plan.team || "Puckly practice plan",
        "practice",
      ).catch((error) => {
        shareFile.current = undefined;
        setShareStatus("error");
        throw error;
      });
      void shareFile.current.then(() => setShareStatus(""));
    }
    return shareFile.current;
  };

  const sharePractice = async () => {
    try {
      const file = await prepareShareFile();
      if (!file) return;
      const result = await sharePdfFile(
        file,
        plan.team || "Puckly practice plan",
      );
      if (result === "downloaded") setShareStatus("downloaded");
    } catch (error) {
      if ((error as DOMException).name !== "AbortError")
        setShareStatus("error");
    }
  };

  const exportPractice = () => {
    const previousTitle = document.title;
    document.title = plan.team.trim() || "Puckly practice plan";
    const restoreTitle = () => {
      document.title = previousTitle;
    };
    window.addEventListener("afterprint", restoreTitle, { once: true });
    window.print();
  };

  useEffect(() => {
    shareFile.current = undefined;
    setShareStatus("");
    const timer = window.setTimeout(() => void prepareShareFile(), 900);
    return () => window.clearTimeout(timer);
    // Generate from the latest rendered plan; a change invalidates the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  useEffect(() => {
    savePlan(plan);
    const timer = window.setTimeout(() => {
      void saveCloudPracticePlan(plan);
    }, 650);
    return () => window.clearTimeout(timer);
  }, [plan]);

  useEffect(() => {
    const templateId = searchParams.get("template");
    if (!templateId) return;
    const template = loadPracticeTemplates().find(
      (item) => item.id === templateId,
    );
    if (template) {
      setPlan(usePracticeTemplate(template));
      setActiveTemplateId(template.id);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const drillId = searchParams.get("drillId");
    const stationIndex = Number(searchParams.get("station"));
    const drill = drillId ? drillById.get(drillId) : undefined;
    if (
      !drill ||
      !Number.isInteger(stationIndex) ||
      stationIndex < 0 ||
      stationIndex >= plan.stations.length
    )
      return;

    setPlan((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      stations: current.stations.map((station, index) =>
        index === stationIndex
          ? {
              ...station,
              drillId: drill.id,
              title: drill.title,
              durationMinutes: drill.durationMinutes,
              description: drill.instructions || drill.objective,
              focus: drill.coachingPoints || drill.objective,
            }
          : station,
      ),
    }));
    setSearchParams({}, { replace: true });
  }, [drillById, plan.stations.length, searchParams, setSearchParams]);

  const updatePlan = (update: Partial<PracticePlan>) =>
    setPlan((current) => ({
      ...current,
      ...update,
      updatedAt: new Date().toISOString(),
    }));

  const updateStation = (index: number, update: Partial<PracticeStation>) =>
    setPlan((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      stations: current.stations.map((station, stationIndex) =>
        stationIndex === index ? { ...station, ...update } : station,
      ),
    }));

  const chooseDrill = (index: number, drillId: string) => {
    const drill = drillById.get(drillId);
    if (!drill) {
      updateStation(index, { drillId });
      return;
    }
    updateStation(index, {
      drillId,
      title: drill.title,
      durationMinutes: drill.durationMinutes,
      description: drill.instructions || drill.objective,
      focus: drill.coachingPoints || drill.objective,
    });
  };

  const selectBrowsedDrill = (drillId: string) => {
    if (browseStation === null) return;
    chooseDrill(browseStation, drillId);
    setBrowseStation(null);
    setBrowseQuery("");
    setBrowseCategory("all");
  };

  const addStation = () =>
    setPlan((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      stations: [...current.stations, emptyStation(current.stations.length)],
    }));

  const removeStation = (index: number) => {
    if (plan.stations.length <= 1) return;
    if (!window.confirm(`Remove drill row ${index + 1} from this practice?`))
      return;
    setPlan((current) => ({
      ...current,
      updatedAt: new Date().toISOString(),
      stations: current.stations.filter(
        (_, stationIndex) => stationIndex !== index,
      ),
    }));
  };

  const saveTemplate = (saveNew: boolean) => {
    const result = savePracticeTemplate({
      ...plan,
      id: !saveNew && activeTemplateId ? activeTemplateId : "current-practice",
    });
    void saveCloudPracticePlan(result.template);
    setActiveTemplateId(result.template.id);
    setTemplateSaved(true);
    window.setTimeout(() => setTemplateSaved(false), 1800);
  };

  return (
    <main className="practice-planner-page">
      <div className="planner-toolbar no-print">
        <div>
          <p className="eyebrow">Practice planner</p>
          <h1>Practice plan</h1>
          <p>
            Build the plan from saved drills, then export a clean PDF for the
            rink.
          </p>
        </div>
        <div className="planner-actions">
          <Link className="text-button" to="/drills">
            Drill library
          </Link>
          <Link className="text-button" to="/practice-templates">
            Templates
          </Link>
          <button
            className="secondary-button"
            onClick={() => saveTemplate(false)}
          >
            Save
          </button>
          <button
            className="secondary-button"
            onClick={() => saveTemplate(true)}
          >
            Save New
          </button>
          {templateSaved && (
            <span className="template-save-status" role="status">
              Saved
            </span>
          )}
          <button className="primary-button" onClick={exportPractice}>
            Export PDF
          </button>
          <button
            className="primary-button share-practice-button"
            disabled={shareStatus === "preparing"}
            aria-busy={shareStatus === "preparing"}
            onPointerEnter={() => void prepareShareFile()}
            onPointerDown={() => void prepareShareFile()}
            onClick={() => void sharePractice()}
          >
            Share PDF
          </button>
        </div>
      </div>

      <section className="practice-document" aria-label="Practice plan">
        <header className="practice-plan-header">
          <div className="practice-summary">
            <div className="practice-title-field">
              <label htmlFor="practice-team" className="header-field-label">
                Practice
              </label>
              <input
                id="practice-team"
                value={plan.team}
                onChange={(event) => updatePlan({ team: event.target.value })}
              />
              <span className="print-field-value print-practice-title">
                {plan.team || "Puckly practice"}
              </span>
            </div>
            <label className="practice-age-field">
              <span className="header-field-label">Age group</span>
              <select
                value={plan.ageGroup}
                onChange={(event) =>
                  updatePlan({
                    ageGroup: event.target.value as PracticePlan["ageGroup"],
                  })
                }
              >
                {AGE_GROUPS.map((age) => (
                  <option value={age} key={age}>
                    {age}
                  </option>
                ))}
              </select>
              <span className="print-field-value">{plan.ageGroup}</span>
            </label>
            <div className="practice-meta-grid">
              <label>
                <span className="header-field-label">Date</span>
                <input
                  type="date"
                  value={plan.date}
                  onChange={(event) => updatePlan({ date: event.target.value })}
                />
                <span className="print-field-value">
                  {formatPracticeDate(plan.date)}
                </span>
              </label>
              <label>
                <span className="header-field-label">Start</span>
                <input
                  type="time"
                  value={plan.startTime}
                  onChange={(event) =>
                    updatePlan({ startTime: event.target.value })
                  }
                />
                <span className="print-field-value">
                  {plan.startTime || "—"}
                </span>
              </label>
              <label>
                <span className="header-field-label">End</span>
                <input
                  type="time"
                  value={plan.endTime}
                  onChange={(event) =>
                    updatePlan({ endTime: event.target.value })
                  }
                />
                <span className="print-field-value">{plan.endTime || "—"}</span>
              </label>
              <span className="practice-total">
                <span className="header-field-label">Total</span>
                <strong>{totalMinutes} min</strong>
              </span>
            </div>
            <label className="practice-wide-field">
              Practice theme / goals
              <input
                value={plan.theme}
                onChange={(event) => updatePlan({ theme: event.target.value })}
                placeholder="Skating fundamentals, puck control, teamwork…"
              />
              <span className="print-field-value">{plan.theme || "—"}</span>
            </label>
            <label className="practice-wide-field">
              Equipment required
              <input
                value={plan.equipment}
                onChange={(event) =>
                  updatePlan({ equipment: event.target.value })
                }
                placeholder="Pucks, cones, tires, goals…"
              />
              <span className="print-field-value">{plan.equipment || "—"}</span>
            </label>
          </div>
          <div className="practice-header-brand" aria-hidden="true">
            <img
              className="practice-puckly-logo"
              src="/brand/puckly-logo-navy.png"
              alt=""
            />
            <img
              className="practice-falcons-logo"
              src="/brand/copenhagen-falcons.png"
              alt=""
            />
          </div>
          <PracticeLegend />
        </header>

        <div className="practice-stations">
          {plan.stations.map((station, index) => {
            const drill = drillById.get(station.drillId);
            const recentDrills = eligibleDrills.slice(0, 5);
            const currentIsRecent = recentDrills.some(
              (item) => item.id === station.drillId,
            );
            return (
              <article className="practice-station" key={index}>
                <div className="station-heading">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <label className="station-title-field">
                    <strong>Drill:</strong>
                    <input
                      aria-label={`Station ${index + 1} title`}
                      value={
                        station.title === "Untitled drill" ? "" : station.title
                      }
                      placeholder="Untitled drill"
                      onChange={(event) =>
                        updateStation(index, { title: event.target.value })
                      }
                      onBlur={() => {
                        if (!station.title.trim())
                          updateStation(index, { title: "Untitled drill" });
                      }}
                    />
                    <span className="station-title-output">
                      {station.title.trim() || "Untitled drill"}
                    </span>
                  </label>
                  <label>
                    Time
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={station.durationMinutes}
                      onChange={(event) =>
                        updateStation(index, {
                          durationMinutes: Math.max(
                            1,
                            Number(event.target.value),
                          ),
                        })
                      }
                    />
                    <span className="station-time-output">
                      {station.durationMinutes}
                    </span>
                    <span>min</span>
                  </label>
                  <button
                    type="button"
                    className="station-remove-button no-print"
                    onClick={() => removeStation(index)}
                    aria-label={`Remove station ${index + 1}`}
                    title="Remove this drill row"
                  >
                    ×
                  </button>
                </div>

                <div className="station-body">
                  <div className="station-rink-column">
                    <label className="station-drill-picker no-print">
                      Saved drill
                      <select
                        value={station.drillId}
                        onChange={(event) => {
                          if (event.target.value === "__browse__") {
                            setBrowseStation(index);
                            return;
                          }
                          chooseDrill(index, event.target.value);
                        }}
                      >
                        <option value="">Blank half ice</option>
                        {drill && !currentIsRecent && (
                          <option value={drill.id}>
                            {drill.title} · Current
                          </option>
                        )}
                        <optgroup label={`Recent ${plan.ageGroup} drills`}>
                          {recentDrills.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </optgroup>
                        <option value="__browse__">Browse drills…</option>
                      </select>
                    </label>
                    <Link
                      className={`station-rink station-rink-link station-rink-${drill?.rink ?? "half"}`}
                      to={
                        drill
                          ? `/drills/${drill.id}/edit?practiceStation=${index}`
                          : `/drills/new?practiceStation=${index}`
                      }
                      aria-label={`${drill ? "Edit" : "Draw"} station ${index + 1} drill`}
                    >
                      <RinkDiagram
                        template={drill?.rink ?? "half"}
                        rinkSide={drill?.rinkSide ?? "left"}
                        elements={drill?.elements ?? []}
                      />
                      <span className="station-rink-action no-print">
                        {drill ? "Edit drill" : "Draw drill"}
                      </span>
                    </Link>
                  </div>

                  <div className="station-notes">
                    <div
                      className={`note-field note-size-${station.descriptionSize}`}
                    >
                      <div className="note-field-header">
                        <span>Description</span>
                        <select
                          className="note-size-select no-print"
                          aria-label={`Station ${index + 1} description font size`}
                          value={station.descriptionSize}
                          onChange={(event) =>
                            updateStation(index, {
                              descriptionSize: event.target
                                .value as PracticeStation["descriptionSize"],
                            })
                          }
                        >
                          <option value="small">Small</option>
                          <option value="normal">Normal</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                      <PracticeTextEditor
                        value={station.description}
                        onChange={(description) =>
                          updateStation(index, { description })
                        }
                      />
                    </div>
                    <div
                      className={`note-field note-field-focus note-size-${station.focusSize}`}
                    >
                      <div className="note-field-header">
                        <span>Focus</span>
                        <select
                          className="note-size-select no-print"
                          aria-label={`Station ${index + 1} focus font size`}
                          value={station.focusSize}
                          onChange={(event) =>
                            updateStation(index, {
                              focusSize: event.target
                                .value as PracticeStation["focusSize"],
                            })
                          }
                        >
                          <option value="small">Small</option>
                          <option value="normal">Normal</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                      <textarea
                        aria-label="Focus"
                        value={station.focus}
                        onChange={(event) =>
                          updateStation(index, { focus: event.target.value })
                        }
                        placeholder="Key coaching cues…"
                      />
                      <FormattedPracticeText value={station.focus} />
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        <button
          type="button"
          className="add-station-button no-print"
          onClick={addStation}
        >
          <span aria-hidden="true">＋</span>
          Add new drill row
        </button>
      </section>
      {browseStation !== null && (
        <div className="drill-browser-backdrop no-print" role="presentation">
          <section
            className="drill-browser-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Browse ${plan.ageGroup} drills`}
          >
            <button
              className="drill-browser-close"
              onClick={() => setBrowseStation(null)}
              aria-label="Close drill browser"
            >
              ×
            </button>
            <p className="eyebrow">Station {browseStation + 1}</p>
            <h2>Browse {plan.ageGroup} drills</h2>
            <div className="drill-browser-filters">
              <input
                type="search"
                value={browseQuery}
                onChange={(event) => setBrowseQuery(event.target.value)}
                placeholder="Search name, folder or focus…"
                aria-label="Search drills"
              />
              <select
                value={browseCategory}
                onChange={(event) =>
                  setBrowseCategory(
                    event.target.value as "all" | DrillFocusArea,
                  )
                }
                aria-label="Filter drill focus area"
              >
                <option value="all">All categories</option>
                {DRILL_FOCUS_AREAS.map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="drill-browser-list">
              {browsableDrills.map((item) => (
                <button
                  key={item.id}
                  onClick={() => selectBrowsedDrill(item.id)}
                >
                  <span className="drill-browser-preview" aria-hidden="true">
                    <RinkDiagram
                      template={item.rink}
                      rinkSide={item.rinkSide}
                      elements={item.elements}
                    />
                  </span>
                  <span className="drill-browser-copy">
                    <strong>{item.title}</strong>
                    <small>
                      {item.focusAreas.join(" · ") || "No focus selected"}
                      {item.folder ? ` · ${item.folder}` : ""}
                    </small>
                    <small>{item.ageGroups.join(" · ")}</small>
                  </span>
                </button>
              ))}
              {!browsableDrills.length && (
                <p>No {plan.ageGroup} drills match these filters.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
